import { useLocation } from "react-router-dom";
import { SidebarNav } from "@/components/ui/sidebar/sidebar-nav";
import { toast } from "sonner";
import { useBusinessTypeFeatures } from "@/hooks/useBusinessTypeFeatures";
import { SidebarMenuProps, OwnerLink } from "./types";
import { SidebarLink } from "./SidebarLink";
import { SidebarEyeButton } from "./SidebarEyeButton";

export const SidebarMenu = ({
  links,
  userType,
  isInventoryActive,
  visibleMenuItems,
  authorizedFeatures,
  onEyeClick,
  shouldShowEyeIcon,
  isFeatureVisible,
  accessCodes,
}: SidebarMenuProps) => {
  const location = useLocation();
  const { hasFeatureAccess } = useBusinessTypeFeatures();

  const isLinkActive = (href: string) => {
    if (href === '/owner' && location.pathname.startsWith('/owner/')) {
      return true;
    }
    return location.pathname === href;
  };

  const handleLinkClick = (e: React.MouseEvent, href: string, feature?: string) => {
    if (isInventoryActive && href !== "/owner/stock") {
      e.preventDefault();
      toast.error("Veuillez terminer l'inventaire en cours avant de naviguer");
      return;
    }

    if (userType === "owner" && feature && feature !== "dashboard") {
      if (!hasFeatureAccess(feature)) {
        e.preventDefault();
        toast.error("Cette fonctionnalité n'est pas disponible pour votre type d'entreprise");
        return;
      }
    }
  };

  const isLinkDisabled = (feature?: string) => {
    if (isInventoryActive) return true;
    if (userType === "owner" && feature && feature !== "dashboard") {
      if (!hasFeatureAccess(feature)) return true;
    }
    return false;
  };

  const shouldShowEyeIconForFeature = (feature?: string) => {
    if (!feature || feature === "dashboard") return false;
    return feature === "settings" || feature === "budget";
  };

  const isFeatureVisibleUpdated = (name: string, feature?: string) => {
    // Si c'est les paramètres ou le budget, utiliser la logique de visibilité existante
    if (feature === "settings" || feature === "budget") {
      const hasAccessCode = accessCodes[feature];
      const isVisible = visibleMenuItems.includes(name);
      
      // Si le feature a un code d'accès et n'est pas visible, on masque l'onglet
      if (hasAccessCode && !isVisible) {
        return false;
      }
    }
    // Pour tous les autres éléments, toujours visible
    return true;
  };

  return (
    <SidebarNav>
      {links.map((item) => (
        <div key={item.name} className="flex items-center gap-2 px-2">
          <SidebarLink
            item={item}
            isActive={isLinkActive(item.href)}
            isDisabled={isLinkDisabled((item as OwnerLink).feature)}
            onClick={(e) => handleLinkClick(e, item.href, (item as OwnerLink).feature)}
            className={!isFeatureVisibleUpdated(item.name, (item as OwnerLink).feature) ? "hidden" : undefined}
          />
          {userType === "owner" && shouldShowEyeIconForFeature((item as OwnerLink).feature) && (
            <SidebarEyeButton
              name={item.name}
              feature={(item as OwnerLink).feature}
              isVisible={visibleMenuItems.includes(item.name)}
              onEyeClick={onEyeClick}
            />
          )}
        </div>
      ))}
    </SidebarNav>
  );
};